import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabase';

const EditProfile = ({ onClose, onSave }) => {
  const { userProfile, updateUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    username: userProfile?.username || '',
    full_name: userProfile?.full_name || '',
    avatar_url: userProfile?.avatar_url || '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(userProfile?.avatar_url || '');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona un archivo de imagen');
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen es muy grande. El tamaño máximo es 5MB');
        return;
      }

      setAvatarFile(file);

      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;

    try {
      setUploading(true);

      // Crear un nombre único para el archivo
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${userProfile.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Subir el archivo al bucket
      const { error: uploadError } = await supabase.storage
        .from('fotos_perfil')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      // Obtener la URL pública
      const { data: publicUrlData } = supabase.storage
        .from('fotos_perfil')
        .getPublicUrl(filePath);

      return publicUrlData.publicUrl;
    } catch (error) {
      console.error('Error en uploadAvatar:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let avatarUrl = formData.avatar_url;

      // Si hay un archivo seleccionado, subirlo primero
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
        if (!avatarUrl) {
          throw new Error('No se pudo obtener la URL del avatar');
        }
      }

      const { error } = await supabase
        .from('users')
        .update({
          username: formData.username,
          full_name: formData.full_name,
          avatar_url: avatarUrl,
        })
        .eq('id', userProfile.id);

      if (error) throw error;

      // Forzar actualización del contexto
      await updateUserProfile();

      // Pequeño delay para asegurar que el contexto se actualice
      await new Promise(resolve => setTimeout(resolve, 300));

      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      alert('Error al actualizar el perfil: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: '#1f2937',
        borderRadius: '12px',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
        color: 'white',
      }}>
        <h2 style={{ marginBottom: '20px', fontSize: '24px', fontWeight: 'bold' }}>
          Editar Perfil
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Nombre de usuario
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #4b5563',
                backgroundColor: '#374151',
                color: 'white',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Nombre completo
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #4b5563',
                backgroundColor: '#374151',
                color: 'white',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Foto de perfil
            </label>

            {/* Preview de la imagen */}
            {previewUrl && (
              <div style={{
                marginBottom: '15px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #8b5cf6',
                  }}
                />
              </div>
            )}

            {/* Input de archivo */}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={uploading || loading}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #4b5563',
                backgroundColor: '#374151',
                color: 'white',
                fontSize: '16px',
                cursor: uploading || loading ? 'not-allowed' : 'pointer',
              }}
            />
            <p style={{
              fontSize: '12px',
              color: '#9ca3af',
              marginTop: '5px',
              marginBottom: 0
            }}>
              Formatos: JPG, PNG, GIF (máximo 5MB)
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #6b7280',
                backgroundColor: 'transparent',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500',
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#8b5cf6',
                color: 'white',
                cursor: (loading || uploading) ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                opacity: (loading || uploading) ? 0.6 : 1,
              }}
            >
              {uploading ? 'Subiendo imagen...' : loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;
